import React, { useEffect, useState } from "react";
import "../CommonTable/CommonTable.scss";
import RoundCheck from "../../../assets/images/vactor/green-check-round.svg";
import FreeIcon from "../../../assets/images/vactor/free-icon.svg";
import DotsMenu from "../../../assets/images/vactor/dot-menu.svg";

import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ArrowBack, ArrowForward, Search } from "@mui/icons-material";
import { PrimaryButton } from "../../AllButtons/AllButtons";
import { useAppDispatch } from "../../../Store/store";
import { downloadUserList, getUserList, viewUser } from "../../../Services/admin.service";
import { resolveAssetUrl } from "../../../Utils/url";
import { useNavigate } from "react-router";
import Loader from "../../Loader/Loader/Loader";

interface Data {
  id: number;
  name: string;
  email: string;
  institution: string;
  status: string;
  time: number;
  action: string;
}

// API Response interface
interface ApiUser {
  id: string;
  name: string;
  email: string;
  institution?: string;
  is_paid: boolean;
  created_at: string;
  grade: number;
  birth_date: string;
  total_time_spent: string;
}

type Order = "asc" | "desc";

const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T): number => {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  if (typeof aValue === "string" && typeof bValue === "string") {
    return bValue.localeCompare(aValue, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
};

const getComparator = <Key extends keyof any>(
  order: Order,
  orderBy: Key
): ((a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const CommonTable = () => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("name");
  const [selected, setSelected] = React.useState<readonly number[]>([]);

  const [allUsers, setAllUsers] = useState(true); // Default to show all users
  const [freeUsers, setFreeUsers] = useState(false);
  const [subscribedUsers, setSubscribedUsers] = useState(false);
  const isFilterApplied = !allUsers && (subscribedUsers || freeUsers);

  // State to store API data
  const [apiUsers, setApiUsers] = useState<ApiUser[]>([]);
  const [rows, setRows] = useState<Data[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const usersPerPage = 10;

  // State for menu and selected user
  const [moreMenu, setMoreMenu] = React.useState<null | HTMLElement>(null);
  const [selectedUserEmail, setSelectedUserEmail] = React.useState<any>("");
  const [viewUserData, setViewUserData] = React.useState<any>(null);
  const [hasSorted, setHasSorted] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const open = Boolean(moreMenu);

  const handleClickMoreMenu = (event: React.MouseEvent<HTMLButtonElement>, email: string) => {
    setMoreMenu(event.currentTarget);
    setSelectedUserEmail(email);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenu(null);
    setSelectedUserEmail("");
  };

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setHasSorted(true);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.map((n) => n.id));
    } else {
      setSelected([]);
    }
  };

  const handleClick = (id: number) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  // Filter menu handlers
  const [filter, setFilter] = React.useState<null | HTMLElement>(null);
  const openFilter = Boolean(filter);
  const handleClickFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilter(event.currentTarget);
  };
  const handleCloseFilter = () => {
    setFilter(null);
  };

  // Filter handlers
  const handleFilterAllUsers = () => {
    setAllUsers(true);
    setFreeUsers(false);
    setSubscribedUsers(false);
    handleCloseFilter();
  };

  const handleFilterSubscribedUsers = () => {
    setAllUsers(false);
    setFreeUsers(false);
    setSubscribedUsers(true);
    handleCloseFilter();
  };

  const handleFilterFreeUsers = () => {
    setAllUsers(false);
    setFreeUsers(true);
    setSubscribedUsers(false);
    handleCloseFilter();
  };

  // Get current filter label
  const getCurrentFilterLabel = () => {
    if (subscribedUsers) return "Subscribed Users";
    if (freeUsers) return "Free Users";
    return "All Users";
  };

  const navigate = useNavigate();

  // Function to handle view user click
  const handleViewUser = async () => {
    if (selectedUserEmail) {
      try {
        localStorage.setItem('email', selectedUserEmail);
        const response = await dispatch(viewUser(selectedUserEmail));
        setViewUserData(response.payload);
        handleCloseMoreMenu();
        navigate('/admin/view_user_details');
      } catch (error) {
        console.error("Error fetching user details:", error);
        handleCloseMoreMenu();
      }
    }
  };

  // Function to fetch users data
  const fetchUsers = (
    pageNumber: number,
    search: string = searchQuery
  ) => {
    setLoading(true);

    let subscription_filter = "";
    if (subscribedUsers) subscription_filter = "subscribed";
    if (freeUsers) subscription_filter = "free";

    dispatch(getUserList({
      users_per_page: usersPerPage,
      page_number: pageNumber,
      search: search || undefined,
      subscription_filter: subscription_filter || undefined
    }))
      .then((res) => {
        const users = res?.payload?.data?.users || [];
        const totalPagesFromAPI = res?.payload?.data?.total_pages || 1;
        const currentPageFromAPI = res?.payload?.data?.current_page || 1;
        // Calculate total users based on pages (approximation)
        const estimatedTotalUsers = totalPagesFromAPI * usersPerPage;

        setApiUsers(users); // Store original API data
        const transformedRows = transformApiDataToRows(users);
        setRows(transformedRows); // Set transformed data for table
        setTotalUsers(estimatedTotalUsers);
        setTotalPages(totalPagesFromAPI);
        setCurrentPage(currentPageFromAPI);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error:", err);
        setLoading(false);
        // Fallback to empty array on error
        setApiUsers([]);
        setRows([]);
        setTotalUsers(0);
        setTotalPages(0);
      });
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1, searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle filter changes
  useEffect(() => {
    if (!loading) { // Avoid initial double fetch if possible, though mount effect covers it
      fetchUsers(1, searchQuery);
    }
  }, [subscribedUsers, freeUsers, allUsers]);

  // Handle pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    fetchUsers(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchUsers(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchUsers(currentPage - 1);
    }
  };

  // Function to transform API data to table format
  const transformApiDataToRows = (apiUsers: ApiUser[]): Data[] => {
    return apiUsers.map((user, index) => ({
      id: index + 1, // Using index as numeric ID, or you could hash the string ID
      name: user.name,
      email: user.email,
      institution: user.institution || "-",
      status: user.is_paid ? "Subscribed" : "Free",
      time: parseInt(user.total_time_spent) || 0, // Extract hours from string like "0 hours"
      action: "",
    }));
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Initial fetch is now handled by the filter/search effects
  }, [dispatch]);

  // Filter rows removed as it's backend-side now
  const visibleRows = React.useMemo(() => {
    if (hasSorted) {
      return [...rows].sort(getComparator(order, orderBy));
    }
    return rows;
  }, [rows, hasSorted, order, orderBy]);


  const onClickDownload = async () => {
    setDownloading(true);
    try {
      const response = await dispatch(downloadUserList());

      if (downloadUserList.fulfilled.match(response)) {
        const payload = response.payload as {
          download_url?: string;
          file_name?: string;
        };
        const downloadUrl = resolveAssetUrl(payload?.download_url);
        if (!downloadUrl) {
          throw new Error("Download URL not returned");
        }

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download =
          payload?.file_name || `Interactions_Export_${new Date().toISOString().split("T")[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log("Download started successfully");
      } else {
        alert("Download failed. Please try again.");
        setDownloading(false);
      }
    } catch (error) {
      console.error("Error during download:", error);
      alert("Failed to download data. Please try again.");
      setDownloading(false);
    } finally {
      setDownloading(false);
    }
  };
  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="common-table-wrapper">
        <div className="user-management-table">
          <div className="common-table-filter-block">
            <TextField
              className="table-search-field"
              placeholder="Search by Name, Email, or Institution..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <Button className={`table-filter-btn${isFilterApplied ? " active-filter" : ""}`}
              id="filterMenu"
              aria-controls={openFilter ? 'filter' : undefined}
              aria-haspopup="true"
              aria-expanded={openFilter ? 'true' : undefined}
              onClick={handleClickFilter}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.75 0V1.5H13L9.25 7.125V13.5H4.75V7.125L1 1.5H0.25V0H13.75ZM2.803 1.5L6.25 6.6705V12H7.75V6.6705L11.197 1.5H2.803Z"
                  fill="#525866"
                />
              </svg>{" "}
            </Button>
            <PrimaryButton
              onClick={onClickDownload}
              loading={downloading}
            >
              Download
            </PrimaryButton>
            <Menu
              className="moremenu-popup"
              id="filterMenu"
              anchorEl={filter}
              open={openFilter}
              onClose={handleCloseFilter}
              MenuListProps={{
                "aria-labelledby": "moremunu-button",
              }}
            >
              <MenuItem onClick={handleFilterAllUsers}>
                <span className="moremenu-text">
                  {allUsers && "✓ "}All Users
                </span>
              </MenuItem>
              <MenuItem onClick={handleFilterSubscribedUsers}>
                <span className="moremenu-text">
                  {subscribedUsers && "✓ "}Subscribed Users
                </span>
              </MenuItem>
              <MenuItem onClick={handleFilterFreeUsers}>
                <span className="moremenu-text">
                  {freeUsers && "✓ "}Free Users
                </span>
              </MenuItem>

            </Menu>

            {/* <PrimaryButton>Create Plan</PrimaryButton> */}
          </div>
          <TableContainer>
            <Table aria-label="basic table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {/* <Checkbox
                      color="primary"
                      indeterminate={
                        selected.length > 0 && selected.length < visibleRows.length
                      }
                      checked={visibleRows.length > 0 && selected.length === visibleRows.length}
                      onChange={handleSelectAllClick}
                    /> */}
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleRequestSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    {/* <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() => handleRequestSort("email")}
                      // active={false}
                      hideSortIcon={true}
                    > */}
                    Email Address
                    {/* </TableSortLabel> */}
                  </TableCell>
                  <TableCell align="left">Institution / School</TableCell>
                  <TableCell align="center">Subscription Status</TableCell>
                  <TableCell align="center">Time Spend (Hours)</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length > 0 ? (
                  visibleRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleClick(row.id)}
                      role="checkbox"
                      aria-checked={selected.includes(row.id)}
                      selected={selected.includes(row.id)}
                    >
                      <TableCell padding="checkbox">
                        {/* <Checkbox checked={selected.includes(row.id)} /> */}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="left">{row.email}</TableCell>
                      <TableCell align="left">{row.institution}</TableCell>
                      <TableCell align="center">
                        {<div>
                          {row.status === "Subscribed" ? (
                            <div className="subscription-badge">
                              <img src={RoundCheck} alt="" /> Subscribed
                            </div>
                          ) : (
                            <div className="free-badge">
                              <img src={FreeIcon} alt="" /> Free
                            </div>
                          )}
                        </div>}
                      </TableCell>
                      <TableCell align="center">{row.time}</TableCell>
                      <TableCell align="center">
                        <div>
                          <IconButton
                            edge="end"
                            aria-label="menu"
                            id="moremunu-button"
                            aria-controls={open ? "basic-moremenu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row selection when clicking menu
                              handleClickMoreMenu(e, row.email);
                            }}
                            sx={{ minWidth: "34px" }}
                          >
                            <img src={DotsMenu} alt="" />
                          </IconButton>
                          <Menu
                            className="moremenu-popup"
                            id="basic-moremenu"
                            anchorEl={moreMenu}
                            open={open}
                            onClose={handleCloseMoreMenu}
                            MenuListProps={{
                              "aria-labelledby": "moremunu-button",
                            }}
                          >
                            <MenuItem onClick={handleViewUser}>
                              <span>
                                <svg
                                  width="18"
                                  height="17"
                                  viewBox="0 0 20 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M9.9998 0.9C14.8526 0.9 18.89 4.392 19.7369 9C18.8909 13.608 14.8526 17.1 9.9998 17.1C5.147 17.1 1.1096 13.608 0.262695 9C1.1087 4.392 5.147 0.9 9.9998 0.9ZM9.9998 15.3C11.8353 15.2996 13.6163 14.6761 15.0514 13.5317C16.4864 12.3872 17.4904 10.7894 17.8991 9C17.4889 7.21197 16.4842 5.616 15.0494 4.47302C13.6145 3.33004 11.8343 2.70768 9.9998 2.70768C8.16532 2.70768 6.38512 3.33004 4.95023 4.47302C3.51535 5.616 2.51069 7.21197 2.1005 9C2.50918 10.7894 3.5132 12.3872 4.94823 13.5317C6.38325 14.6761 8.16427 15.2996 9.9998 15.3V15.3ZM9.9998 13.05C8.92567 13.05 7.89554 12.6233 7.13601 11.8638C6.37649 11.1043 5.9498 10.0741 5.9498 9C5.9498 7.92587 6.37649 6.89574 7.13601 6.13622C7.89554 5.3767 8.92567 4.95 9.9998 4.95C11.0739 4.95 12.1041 5.3767 12.8636 6.13622C13.6231 6.89574 14.0498 7.92587 14.0498 9C14.0498 10.0741 13.6231 11.1043 12.8636 11.8638C12.1041 12.6233 11.0739 13.05 9.9998 13.05ZM9.9998 11.25C10.5965 11.25 11.1688 11.0129 11.5908 10.591C12.0127 10.169 12.2498 9.59674 12.2498 9C12.2498 8.40326 12.0127 7.83097 11.5908 7.40901C11.1688 6.98705 10.5965 6.75 9.9998 6.75C9.40306 6.75 8.83076 6.98705 8.40881 7.40901C7.98685 7.83097 7.7498 8.40326 7.7498 9C7.7498 9.59674 7.98685 10.169 8.40881 10.591C8.83076 11.0129 9.40306 11.25 9.9998 11.25Z"
                                    fill="#525866"
                                  />
                                </svg>
                              </span>
                              <span className="moremenu-text">View</span>
                            </MenuItem>
                          </Menu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <div className="no-data-found">No Users Found</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="common-table-pagination">
            {/* <div className="row-per-page">
              Showing <span>{visibleRows.length === 0 ? 0 : ((currentPage - 1) * usersPerPage) + 1}</span> to <span>{Math.min(currentPage * usersPerPage, visibleRows.length)}</span> of <span>{visibleRows.length}</span> {getCurrentFilterLabel().toLowerCase()}
            </div> */}
            <div></div>
            <Stack spacing={2}>
              <Pagination
                variant="outlined"
                shape="rounded"
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    onClick={(e) => {
                      if (item.type === 'previous') {
                        e.preventDefault();
                        handlePreviousPage();
                      } else if (item.type === 'next') {
                        e.preventDefault();
                        handleNextPage();
                      } else if (item.type === 'page' && item.page) {
                        // Handle direct page number clicks
                        e.preventDefault();
                        fetchUsers(item.page);
                      }
                    }}
                    components={{
                      previous: () => <span>Previous</span>,
                      next: () => <span>Next</span>,
                    }}
                    sx={{
                      "&.MuiPaginationItem-previous, &.MuiPaginationItem-next":
                      {
                        fontWeight: "bold",
                        color: currentPage === 1 && item.type === 'previous' ? "#ccc" :
                          currentPage === totalPages && item.type === 'next' ? "#ccc" : "blue",
                        cursor: currentPage === 1 && item.type === 'previous' ? "not-allowed" :
                          currentPage === totalPages && item.type === 'next' ? "not-allowed" : "pointer",
                        pointerEvents: (currentPage === 1 && item.type === 'previous') ||
                          (currentPage === totalPages && item.type === 'next') ? "none" : "auto",
                      },
                      "&.MuiPaginationItem-page": {
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#1976d2",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        },
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonTable;
